// @flow

import React, { type ElementProps } from "react";
import { FieldTemplateProps } from "react-jsonschema-form";
import { localize } from "redux-i18n";
import classNames from "classnames";
import Icon from "../../icon";
import { FormGroup, Label, Col } from "reactstrap";
import { TooltipDropdown } from "../../index";

type Props = ElementProps<typeof FieldTemplateProps>;

/**
 *
 * @param props
 * @returns {JSX.Element|Node|*|React.Node}
 * @constructor
 *
 *  You can use ui:options.labelposition === "left", "right OR "vertical"
 *  in the uiSchema to position the label next to the field instead of top
 *
 */

const FieldTemplate = (props: Props) => {
  if (props.schema.type === "object") {
    return props.children;
  }

  if (props.schema.type === "array") {
    return props.children;
  }
  const displayLabel = props.displayLabel ? props.label : null;
  const uiOptions = props.uiSchema["ui:options"] || {};
  const errors = props.errors.props.errors || props.errors.props.rawErrors;
  const labelclass = uiOptions.labelclass || "";
  const labelposition = uiOptions.labelposition;
  const label = uiOptions && uiOptions.label ? uiOptions.label : displayLabel;
  const isDatePicker = props.uiSchema?.["ui:widget"] === "date-picker";
  return (
    <FormGroup
      className={classNames("mb-0 justify-content-center", props.classNames, {
        "d-none": props.hidden,
        "field-offset": uiOptions.withOffset,
        "bottom-offset-none": uiOptions.bottomOffsetNone,
        "d-flex": labelposition === "left",
        "d-flex flex-row-reverse": labelposition === "right",
        "d-flex flex-column": labelposition === "vertical",
      })}
    >
      {props.rawDescription && <div>{props.rawDescription}</div>}
      {!uiOptions.hidelabel && (displayLabel || uiOptions.label) && (
        <Col
          className={classNames("col-xs-12 px-0", {
            "pt-2": !!labelposition,
            "field-template-one-line": label && label.length <= 20,
            "field-template-two-line": label && label.length > 20,
            "field-template-date-picker": isDatePicker,
          })}
        >
          <Label
            htmlFor={props.id}
            className={classNames(
              `field-template-label col-xs-12 ${labelclass} font-weight-medium`,
              {
                "d-flex align-items-center": !!uiOptions.tooltip,
              },
            )}
          >
            <span>
              {label}
              {props.required || (uiOptions && uiOptions.required) ? (
                <Icon
                  icon="asterisk"
                  size="xs"
                  className={classNames("text-warning", {
                    "ml-2": props.displayLabel && props.label,
                  })}
                />
              ) : null}
            </span>
            {uiOptions.tooltip && !uiOptions.tooltip.positionRight && (
              <TooltipDropdown {...uiOptions.tooltip} color="coffee" />
            )}
          </Label>
        </Col>
      )}
      <Col className="p-0">
        {uiOptions?.tooltip?.positionRight ? (
          <div className="position-relative">
            {props.children}
            {uiOptions.tooltip && (
              <div className="right-tooltip">
                <TooltipDropdown {...uiOptions.tooltip} color="coffee" />
              </div>
            )}
          </div>
        ) : (
          props.children
        )}
        {props.help}
        {errors &&
          errors.map(e => (
            <div key={e} className="error-msg text-danger">
              {props.t(e)}
            </div>
          ))}
      </Col>
    </FormGroup>
  );
};

export default localize()(FieldTemplate);
